import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomDatatable from '../CustomDatatable';
import { NoDataFoundIcon, PencilSquareIcon } from '../../assets/Icons';
import LoadingSpinner from '../LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { URLS } from '../../_config';

interface WineInfiniteTableProps {
  data: any[];
  hasMore: boolean;
  isLoading: boolean; // true when table is reloading (search/filter change)
  isAppending?: boolean; // true when appending more rows (infinite scroll)
  onLoadMore: () => void;
  onRowSelect?: (row: any, checked: boolean) => void;
  selectedWineRows?: any[];
  handleWordClick?: (word: string) => void;
  onWineNameClick?: (wineName: string) => void;
  noiseEnabled?: boolean;
  className?: string;
  shouldShowEditWines?: boolean;
  handleEditWine?: (id: string) => void;/*  */
  fromExternalInput?: boolean;
  dataLoaded?: boolean;
  selectedLiteral?: { id: string; literal: string } | null;
  handleLiteralClick?: (id: string, literal: string) => void;
}

const wineColumns = (
  onRowSelect?: (row: any, checked: boolean) => void,
  selectedWineRows?: any[],
  handleWordClick?: (word: string) => void,
  onWineNameClick?: (wineName: string) => void,
  noiseEnabled?: boolean,
  shouldShowEditWines?: boolean,
  handleEditWine?: (id: string) => void,
  fromExternalInput?: boolean,
  dataLoaded?: boolean
) => [
  {
    name: '',
    cell: (row: { id: string; name: string }) => (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          className="red-checkbox"
          checked={selectedWineRows?.some(r => r.id === row.id) || false}
          onChange={e => onRowSelect && onRowSelect(row, e.target.checked)}
        />
        {shouldShowEditWines && handleEditWine && (
          <button
            className="p-1 text-blue-600 hover:text-blue-800 flex items-center gap-1"
            onClick={e => {
              e.stopPropagation();
              console.log('Edit button clicked, row ID:', row.id);
              handleEditWine(row.id);
            }}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    ),
    maxWidth: '80px'
  },
  {
    name: 'ID',
    cell: (row: { id: string; literal: string; name: string; selectedLiteral?: { id: string; literal: string } | null; handleLiteralClick?: (id: string, literal: string) => void; fromExternalInput?: boolean }) => (
      row.handleLiteralClick ? (
        <div
          className={`min-h-[24px] px-2 flex items-center cursor-pointer rounded-full ${row.selectedLiteral && row.selectedLiteral.id === row.id ? 'bg-blue-600 text-white' : ''}`}
          style={{ height: '24px' }}
          onClick={() => row.handleLiteralClick && row.handleLiteralClick(row.id, row.literal)}
        >
          <span className="w-full text-center" style={{ width: '100%', paddingTop: '0.2rem' }}>{row.literal}</span>
        </div>
      ) : (
        <div className="min-h-[24px] px-2" style={{ height: '32px', display: 'flex', alignItems: 'center' }}>{row.literal}</div>
      )
    ),
    maxWidth: '120px'
  },
  {
    name: 'Name',
    cell: (row: { id: string; name: string; nameWords: string[] }) => (
      <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
        {row.nameWords?.map((word: string, idx: number) => (
          <span
            key={idx}
            className="cursor-pointer hover:text-blue-600"
            onClick={() => {
              if (onWineNameClick) {
                onWineNameClick(word);
              } else if (handleWordClick) {
                handleWordClick(word);
              }
            }}
          >
            {word}{' '}
          </span>
        ))}
      </span>
    ),
    style: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }
  }
];

const WineInfiniteTable: React.FC<WineInfiniteTableProps> = ({
  data = [],
  hasMore = false,
  isLoading = false,
  onLoadMore,
  onRowSelect,
  selectedWineRows = [],
  handleWordClick,
  onWineNameClick,
  noiseEnabled = false,
  className = '',
  shouldShowEditWines,
  handleEditWine: propHandleEditWine,
  fromExternalInput = false,
  dataLoaded = false,
  selectedLiteral,
  handleLiteralClick
}) => {
  const navigate = useNavigate();

  const handleEditWine = (id: string) => {
    console.log('handleEditWine func called with ID:', id);
    if (propHandleEditWine) {
      propHandleEditWine(id);
    } else {
      // If we're coming from external input, pass that info in the URL
      if (fromExternalInput) {
        navigate(URLS.WINE_ADDITION.EDIT_WINE.replace(':id', id) + `?fromExternalInput=true&dataLoaded=${dataLoaded}`, {
          state: { fromEditButton: true }
        });
      } else {
        navigate(URLS.WINE_ADDITION.EDIT_WINE.replace(':id', id), {
          state: { fromEditButton: true }
        });
      }
    }
  };

  // Add selectedLiteral and handleLiteralClick to each row
  const processedWineDbs = React.useMemo(
    () =>
      data.map(row => ({
        ...row,
        nameWords: row?.wine_name ? `${row.wine_name}`.split(/\s+/) : row?.name ? row.name.split(/\s+/) : [],
        literal: row.literal,
        selectedLiteral,
        handleLiteralClick,
        fromExternalInput
      })),
    [data, selectedLiteral, handleLiteralClick, fromExternalInput]
  );

  return (
    <div id="wine-infinite-table" className="flex-1 min-h-0 border border-gray-200 rounded bg-gray-50 overflow-auto flex flex-col relative">
      {isLoading && processedWineDbs.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 min-h-full h-full w-full">
          <LoadingSpinner size="40" />
        </div>
      ) : processedWineDbs.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 min-h-full h-full w-full">
          <NoDataFoundIcon className="w-8 h-8 text-gray-400" />
          <span className="text-gray-500 text-xs">No Wines</span>
        </div>
      ) : (
        <div className="relative flex-1 h-full">
          <InfiniteScroll
            dataLength={processedWineDbs.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={null}
            scrollableTarget="wine-infinite-table"
            className="h-full"
          >
            <CustomDatatable
              columns={wineColumns(
                onRowSelect,
                selectedWineRows,
                handleWordClick,
                onWineNameClick,
                noiseEnabled,
                shouldShowEditWines,
                handleEditWine,
                fromExternalInput,
                dataLoaded
              )}
              data={processedWineDbs}
              fixedHeight={false}
              className={className}
            />
          </InfiniteScroll>
          {/* Overlay only when loading and data exists */}
          {isLoading && processedWineDbs.length > 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
              <LoadingSpinner size="40" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WineInfiniteTable;